"use client";

import { useState } from "react";
import { Play } from "lucide-react";
import { useAuth } from "@clerk/nextjs";
import ContinueStudyDialog from "@/components/home/ContinueStudyDialog";
import GuestStudyStartDialog from "@/components/home/GuestStudyStartDialog";
import { InAppBrowserGuideContent } from "@/components/InAppBrowserGuide";
import { isInAppBrowser } from "@/lib/inapp-browser";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * 홈 히어로 "공부 시작하기" CTA (JCG-2166, 비회원 흐름 JCG-2167).
 *
 * - 로그인: 이어학습 모달(ContinueStudyDialog)을 연다 — 카테고리별 최근 학습 페이지 선택지.
 * - 비로그인: 무료 기능 소개 모달(GuestStudyStartDialog)을 띄워 두 갈래로 안내한다.
 *   · "회원가입하기" → `/sign-up` 페이지로 이동(가입 전환 트래커 동작을 위해 페이지 이동 필수).
 *   · "회원가입없이 보기" → 소개 모달을 닫고 ContinueStudyDialog(이어학습 모달)를 연다.
 *     비로그인 시 getLastStudiedPages()가 빈 배열을 반환하므로 4개 카테고리 인덱스 폴백으로 안전.
 *   단, 카카오톡·인스타 등 인앱 브라우저에서는 외부 브라우저 안내 다이얼로그를 먼저 띄운다
 *   (clerk-auth-ui.md §4 인앱 가드 — 가입 후 로그인 OAuth 차단 회피).
 *
 * HomeHero(서버 컴포넌트)가 기존 `<Link href="/theory/4week-study-plan">` 자리에 이 버튼을
 * 끼워 넣는다. 버튼 스타일은 기존 CTA(amber→orange 그라데이션 pill)를 그대로 유지한다.
 */
export default function StudyStartButton() {
  const { isLoaded, isSignedIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [inAppGuideOpen, setInAppGuideOpen] = useState(false);

  const handleClick = () => {
    // Clerk 하이드레이션 전(isSignedIn === undefined) 클릭 무시 — 로그인 사용자가 모달 대신
    // 비회원 흐름으로 잘못 빠지는 것을 방지(다음 클릭은 로드 완료 후 정상 동작).
    if (!isLoaded) return;
    if (isSignedIn) {
      setOpen(true);
    } else if (isInAppBrowser()) {
      // 인앱 브라우저: 가입 후 Clerk OAuth 차단 → 외부 브라우저 안내로 유도.
      setInAppGuideOpen(true);
    } else {
      // 비로그인: 무료 기능 소개 모달 → 회원가입 / 회원가입없이 보기.
      setGuestOpen(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-orange-500/25 transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
      >
        <Play className="h-4 w-4 fill-white" aria-hidden="true" /> 공부 시작하기
      </button>
      {/* 로그인/비로그인 공용: 비회원도 "회원가입없이 보기"로 이 모달을 연다(빈 이력 → 인덱스 폴백). */}
      <ContinueStudyDialog open={open} onOpenChange={setOpen} />
      <GuestStudyStartDialog
        open={guestOpen}
        onOpenChange={setGuestOpen}
        onBrowseAsGuest={() => {
          setGuestOpen(false);
          setOpen(true);
        }}
      />
      <Dialog open={inAppGuideOpen} onOpenChange={setInAppGuideOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>인앱 브라우저에서는 로그인이 안 돼요</DialogTitle>
            <DialogDescription>외부 브라우저에서 열어주세요</DialogDescription>
          </DialogHeader>
          <InAppBrowserGuideContent />
        </DialogContent>
      </Dialog>
    </>
  );
}
